import "devextreme/dist/css/dx.dark.compact.css";
import { DataGrid } from "devextreme-react";
import {
  Column,
  ColumnChooser,
  FilterRow,
  GroupPanel,
  Scrolling,
  SearchPanel,
  Summary,
  GroupItem,
  Grouping,
} from "devextreme-react/data-grid";
import { forwardRef, useEffect, useState } from "react";

import {
  arrived,
  assignee,
  cycleGoal,
  department,
  deptDateTimeIn,
  deptDateTimeOut,
  hours,
  jobClass,
  jobId,
  timeInDept,
  cycleTime,
  leadTime,
  lagTime,
  productionCycle,
  productionTouchTime,
  touchTime,
  customer,
  groupName,
  year,
  make,
  model,
  scheduledIn,
  scheduledOut,
  datePickup,
  repairStart,
  repairEnd,
  dateReady,
  closeDate,
  refinish,
  total,
  efficiency,
  goalVariance,
} from "../reportColumns";
import useDepartments from "hooks/useDepartments";

const defaultColumns = [
  jobId,
  year,
  make,
  model,
  hours,
  refinish,
  jobClass,
  cycleGoal,
  department,
  timeInDept,
  goalVariance,
  efficiency,
  deptDateTimeIn,
  deptDateTimeOut,
];

// Define column order with visibleIndex
const columnOrder = {
  jobId: 0,
  year: 1,
  make: 2,
  model: 3,
  hours: 4,
  refinish: 5,
  jobClass: 6,
  cycleGoal: 7,
  department: 8,
  timeInDept: 9,
  goalVariance: 10,
  efficiency: 11,
  deptDateTimeIn: 12,
  deptDateTimeOut: 13,  
  customer: 14,
  groupName: 15,
  total: 16,
  scheduledIn: 17,
  scheduledOut: 18,
  arrived: 19,
  datePickup: 20,
  repairStart: 21,
  repairEnd: 22,
  dateReady: 23,
  closeDate: 24,
  cycleTime: 25,
  leadTime: 26,
  lagTime: 27,
  productionCycle: 28,
  productionTouchTime: 29,
  touchTime: 30,
};

const allColumns = [
  jobId,
  year,
  make,
  model,
  hours,
  refinish,
  jobClass,
  cycleGoal,
  department,
  timeInDept,
  goalVariance,
  efficiency,
  deptDateTimeIn,
  deptDateTimeOut,
  assignee,
  customer,
  groupName,
  total,
  scheduledIn,
  scheduledOut,
  arrived,
  datePickup,
  repairStart,
  repairEnd,
  dateReady,
  closeDate,
  cycleTime,
  leadTime,
  lagTime,
  productionCycle,
  productionTouchTime,
  touchTime,
];

// Custom display format function for time-based columns
const formatTimeDisplay = (value) => {
  if (value === 0 || value === null || value === undefined) {
    return "0M";
  }
  
  const totalMinutes = Math.abs(value);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  
  let result = "";
  if (days > 0) {
    result += `${days}D `;
  }
  if (hours > 0) {
    result += `${hours}H `;
  }
  if (minutes > 0 || (days === 0 && hours === 0)) {
    result += `${minutes}M`;
  }
  
  return result.trim();
};

// Custom display format function for goal variance (with negative sign logic)
const formatGoalVarianceDisplay = (value) => {
  if (value === 0 || value === null || value === undefined) {
    return "0M";
  }
  
  const totalMinutes = Math.abs(value);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  
  let result = "";
  if (days > 0) {
    result += `${days}D `;
  }
  if (hours > 0) {
    result += `${hours}H `;
  }
  if (minutes > 0 || (days === 0 && hours === 0)) {
    result += `${minutes}M`;
  }
  
  const formattedTime = result.trim();
  
  // Add negative sign when under goal (positive value)
  if (value > 0) {
    return `-${formattedTime}`;
  }
  
  return formattedTime;
};

// Custom display format function for efficiency
const formatEfficiencyDisplay = (value) => {
  if (value === 0 || value === null || value === undefined) {
    return "0%";
  }
  return `${value.toFixed(1)}%`;
};

const AssigneeLeaderboard = forwardRef((props, ref) => {
  const { chartType, reportData } = props;
  const {departments} = useDepartments()
  const [data, setData] = useState();
  const [dataGridRef, setDataGridRef] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(new Set(defaultColumns.map(col => col.dataField)));

  const getJobClassCycle = (department, jobClassName) => {
    let cycledept = departments?.filter((d) => d.name === department)[0];
    let jobClassCycle = cycledept?.jobClassCycles?.filter(
      (jc) => jc.jobClass.name === jobClassName
    )[0];
    let cycle = ""
    if(jobClassCycle){
    if(jobClassCycle.days > 0){
      cycle += `${jobClassCycle.days}D `;
    }
    if(jobClassCycle.hours > 0){
      cycle += `${jobClassCycle.hours}H `;
    }
    if(jobClassCycle.minutes > 0){
      cycle += `${jobClassCycle.days}M`;
    }
    if(jobClassCycle.days === 0 && jobClassCycle.hours === 0 && jobClassCycle.minutes === 0){
      return "N/A"
    }
    return cycle;
    }
    return "N/A"
  };

  const getJobClassCycleMinutes = (department, jobClassName) => {
    let cycledept = departments?.filter((d) => d.name === department)[0];
    let jobClassCycle = cycledept?.jobClassCycles?.filter(
      (jc) => jc.jobClass.name === jobClassName
    )[0];
    if(jobClassCycle){
      return (jobClassCycle.days * 24 * 60) + (jobClassCycle.hours * 60) + jobClassCycle.minutes;
    }
    return 0;
  };

  const calculateEfficiency = (timeInDept, goalMinutes) => {
    if (goalMinutes === 0 || timeInDept === 0) return 0;
    return (goalMinutes / timeInDept) * 100;
  };

  const calculateGoalVariance = (timeInDept, goalMinutes) => {
    if (goalMinutes === 0) return 0;
    return goalMinutes - timeInDept;
  };

  const handleColumnVisibilityChange = (e) => {
    // Only handle column visibility changes
    if (e.name === 'columns' && e.columns) {
      const newVisibleColumns = new Set();
      e.columns.forEach(col => {
        if (col.visible) {
          newVisibleColumns.add(col.dataField);
        }
      });
      setVisibleColumns(newVisibleColumns);
    }
  }; 

  useEffect(() => {
    if (!!reportData && !!reportData.data) {
      setData(
        reportData.data.map((d) => {
          const goalMinutes = getJobClassCycleMinutes(d.departmentName, d.jobClassName);
          const efficiency = calculateEfficiency(d.timeInDept, goalMinutes);
          const goalVariance = calculateGoalVariance(d.timeInDept, goalMinutes);
          
          return {
            ...d,
            cycleGoal: getJobClassCycle(d.departmentName, d.jobClassName),
            jobId: d.hat !== "" ? d.hat : d.jobAdminInt,
            assignee: `${d.firstName} ${d.lastName}`,
            efficiency: efficiency,
            goalVariance: goalVariance,
          };
        })
      );
    }
  }, [reportData]);

    return (
    <>
      {!!data ? (
                               <DataGrid
            ref={setDataGridRef}
            height="100%"
            width="100%"
            dataSource={data}
            keyExpr="id"
            columnAutoWidth={false}
            showColumnLines={false}
            showRowLines={false}
            showBorders={false}
            rowAlternationEnabled={true}
            allowColumnReordering={true}
            allowSorting={true}
            defaultSorting={[{ selector: "dateTimeIn", desc: true }]}
            onOptionChanged={handleColumnVisibilityChange}
          >
          <Grouping autoExpandAll={true} />
          <GroupPanel visible={true} />
          <Column dataField="assignee" groupIndex={0} />
          <Summary>
            <GroupItem column="jobId" summaryType="count" displayFormat="Jobs: {0}" />
            {visibleColumns.has("hours") && (
              <GroupItem column="hours" summaryType="sum" displayFormat="Hours: {0}" valueFormat="fixedPoint" />
            )}
            {visibleColumns.has("refinish") && (
              <GroupItem column="refinish" summaryType="sum" displayFormat="Refinish: {0}" valueFormat="fixedPoint" />
            )}
            {visibleColumns.has("timeInDept") && (
              <GroupItem column="timeInDept" summaryType="sum" displayFormat="Time In Dept: {0}" valueFormat={formatTimeDisplay} />
            )}
            {visibleColumns.has("goalVariance") && (
              <GroupItem column="goalVariance" summaryType="sum" displayFormat="Over/Under: {0}" valueFormat={formatGoalVarianceDisplay} />
            )}
            {visibleColumns.has("efficiency") && (
              <GroupItem column="efficiency" summaryType="avg" displayFormat="Avg Efficiency: {0}" valueFormat={formatEfficiencyDisplay} />
            )}
          </Summary>
          <Scrolling mode="infinite" />
          <FilterRow visible />
          <SearchPanel visible />
          <ColumnChooser enabled={true} position="center" width={300} height={400} />
                     {allColumns.map((c) => {
             const isDefaultColumn = defaultColumns.some(defaultCol => defaultCol.dataField === c.dataField);
             return (
               <Column 
                 key={c.dataField} 
                 {...c} 
                 visible={isDefaultColumn}
                 visibleIndex={columnOrder[c.dataField]}
               />
             );
           })}
                   </DataGrid>
        ) : (
          <div> No Data to Display </div>
        )}
      </>
    );
  });

export default AssigneeLeaderboard;
